import { useMemo } from 'react';
import { APIResponse, getJSON, post, useGet } from '../services/fetch';

export function useGetTask(id: number | string | undefined) {
  const response = useGet(id ? `/tasks/${id}` : null) as APIResponse<TaskAPISchema>;
  const { status, data, error } = response;
  const apiResponse = useMemo(() => {
    if (status !== 'hasValue' || !data) return null;
    return taskDecorator(data);
  }, [status, data]);

  return { status, data: apiResponse, error };
}

function taskDecorator(response: TaskAPISchema): TaskSchema {
  const included = response.included || [];

  const { data } = response;
  const { attributes, id, relationships } = data;
  const parsedEmail =
    included.find(i => i.type === 'parsed_email' && i.id === relationships.parsedEmail.data?.id)
      ?.attributes || null;
  return {
    ...attributes,
    parsedEmail,
    id,
  };
}
export async function getCurrentTask() {
  const response = (await getJSON(`/task`)) as TaskAPISchema;
  if (!Array.isArray(response.data))
    return { ...response.data.attributes, id: response.data.id } as TaskSchema;
  return null;
}
export async function postPrediction(id: number | string, prediction: string) {
  return (await post(`/predictions`, {
    taskId: id,
    topicRule: prediction,
  })) as PredictionAPISchema;
}
export async function postSkipTask(id: number | string) {
  return await post(`/task/${id}/skip`);
}
interface TaskAPISchema {
  data: {
    attributes: {
      aiPrediction: AIPredictionSchema[];
      body: string;
      ccs: string[];
      receivers: string[];
      sender: string;
      state: 'disputed' | 'crucial' | 'normal';
      subject: string;
      type: string;
    };
    relationships: {
      parsedEmail: {
        data: { type: 'parsed_email'; id: string } | null;
      };
    };
    id: string;
    type: 'tasks';
  };
  included: {
    type: 'parsed_email';
    id: string;
    attributes: ParsedEmailSchema;
  }[];
}

interface AIPredictionSchema {
  accuracy: number;
  confidence: number;
  createdAt: string;
  groundTruthId: number | null;
  id: number;
  sourceId: number;
  sourceType: string;
  taskId: number;
  topicRule: string;
  updatedAt: string;
}

export interface TaskSchema {
  id: string;
  aiPrediction: AIPredictionSchema[];
  body: string;
  ccs: string[];
  receivers: string[];
  sender: string;
  state: 'disputed' | 'crucial' | 'normal';
  subject: string;
  type: string;
  parsedEmail: ParsedEmailSchema | null;
}
interface ParsedEmailSchema {
  subject: string;
  body: string;
  farewell: string;
  greeting: string;
  legal: string;
}
interface PredictionAPISchema {
  data: {
    id: string;
    type: 'predictions';
    attributes: {
      id: number;
      topicRule: Prediction;
      accuracy: number;
      sourceType: string;
      sourceId: number;
      createdAt: string;
      updatedAt: string;
      groundTruthId: string | number | null;
    };
  };
}

export type Prediction =
  | 'positive_intent'
  | 'negative_intent'
  | 'meeting_request'
  | 'ooo'
  | 'bounced'
  | 'referral'
  | 'rfi'
  | 'neutral_intent'
  | 'dnc';

// Keeping this here as a good example of what the payload might look
// const API_TASK_EXAMPLE = {
//   data: {
//     id: '77095',
//     type: 'tasks',
//     attributes: {
//       subject: 'RE: Re:',
//       body: "Sender Sue Milner \u003csusiemilner@hotmail.com\u003e\r\nTo: Sarah Jones \u003csarah.jones@randstad.com.au\u003e\r\nThanks Sarah, \n  \u0026nbsp;  \n Actually I don’t use this address very much. My up to date address is almost the same :\nsusiemilneroz@gmail.com \n  \u0026nbsp;  \n I am interested in flexible options as I current reside in Townsville.\n \n  \u0026nbsp;  \n Kind regards \n  \u0026nbsp;  \n  \u0026nbsp;  \n Susan Milner \n 0431 265 672 \n  \u0026nbsp;  \n Sent from \nMail for Windows \n  \u0026nbsp;  \n \n From: Sarah Jones \nSent: Monday, 1 August 2022 9:16 AM \nTo: susiemilner@hotmail.com \nSubject: Re: \n \n  \u0026nbsp;  \n \n Hi Susan,\u0026nbsp; \n \nI'm guessing from the lack of response that you may not be interested in opportunities at the moment or the role I had wasn't quite the right match, or like many of us you've just been too busy.   \n \n \n If you are open to opportunities and when you get a moment, it would be great to hear from you and let me know how we may be able to support you in your next move. \n \nYou might also like to check our \nwebsite for our latest roles and career advice.   \n \n \n  \nThanks   \n \n  \u0026nbsp;  \n \n Sarah Jones \nvirtual customer success specialist \n \nsarah.jones@randstad.com.au \nwww.randstad.com.au \n \n \n   \n \n \n  \u0026nbsp;  \n \n  \u0026nbsp;  \n \n From: Sarah Jones \u0026lt;sarah.jones@randstad.com.au\u0026gt; \nSent: Thu, Jul 21, 2022 1:38 PM \nTo: susiemilner@hotmail.com \nSubject: Susan, our client has a job I think you’d be interested in   \n \n Hi Susan, \n \nI hope all is well and that you don't mind me reaching out by email, I know everyone is busy these days and it's hard to take a call.\n \n \nI'm Sarah, a Customer Success Specialist at Randstad, and as you are no doubt aware, we currently find ourselves in the busiest market Australia has seen. Randstad's Government team has been working closely with Government departments in Brisbane and is looking\n for new talent in the market.    \n \n  \u0026nbsp;  \n \n Our team is passionate about aligning the career aspirations within Communications, Digital and Media professionals like yourself and we came across your details on our database and have a few roles that I think you'd be interested to hear\n more about. \n \nOur Government clients are currently looking for the below:   \n \n \n\u0026lt;![if !supportLists]\u0026gt;·\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\n\u0026lt;![endif]\u0026gt;AO5 - AO7 Communications Officer | Media Advisors | Digital Communications Officer | Graphic Designers   \n \n\u0026lt;![if !supportLists]\u0026gt;·\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\n\u0026lt;![endif]\u0026gt;6 - 12 month contract roles commencing within 1 - 4 weeks   \n \n\u0026lt;![if !supportLists]\u0026gt;·\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\n\u0026lt;![endif]\u0026gt;Corporate communications, internal communications, external communications, project communications, digital communications, reactive \u0026amp; proactive media, graphic design   \n \n\u0026lt;![if !supportLists]\u0026gt;·\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\n\u0026lt;![endif]\u0026gt;Brisbane CBD and Sunshine Coast    \n \n\u0026lt;![if !supportLists]\u0026gt;·\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\n\u0026lt;![endif]\u0026gt;Flexible and work from home arrangements for the right candidates   \n \n\u0026lt;![if !supportLists]\u0026gt;·\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\u0026nbsp;\n\u0026lt;![endif]\u0026gt;Attractive hourly rates on offer - $55 - $100 per hour plus 10.5% super   \n \n Is the above something that interests you? Would you like to know more or would you know someone else that might be interested? Or are you open to opportunities but the above is not quite right?   \n \n \n  \nPlease let me know, and if it is a yes to wanting to know more about the role or you are looking for another opportunity then please send across your latest CV so we can reach out to have a discussion around what you are looking for and whether this role or\n others are a match.   \n \n \n  \nKind regards,   \n \n  \u0026nbsp;  \n \n Sarah Jones \nvirtual customer success specialist \n \nsarah.jones@randstad.com.au \nwww.randstad.com.au \n \n \n   \n \n \n  \u0026nbsp;  \n \n \n  \nThis email was sent to you\u0026nbsp;because you are a candidate that has registered your details\u0026nbsp;with Randstad and/or as a client, referee or supplier you have provided your contact details to us and have\n consented to receiving communications\u0026nbsp;from Randstad in accordance with our\u0026nbsp;Privacy\n Collection Statement.\u0026nbsp;If you no longer wish to receive these emails you may\u0026nbsp;unsubscribe\u0026nbsp;at\n any time.\u0026nbsp; Please allow 48 hours\u0026nbsp;for your request to be activated.   \n  \u0026nbsp;",
//       ccs: [],
//       sender: 'Sue Milner \u003csusiemilner@hotmail.com\u003e',
//       receivers: ['Sarah Jones \u003csarah.jones@randstad.com.au\u003e'],
//       state: 'crucial',
//       type: 'new',
//       ai_prediction: [
//         {
//           id: 193743,
//           task_id: 77095,
//           topic_rule: 'positive_intent',
//           accuracy: 0.0,
//           source_type: 'Ai',
//           source_id: 1,
//           created_at: '2022-08-01T03:46:08.670Z',
//           updated_at: '2022-08-01T03:46:08.670Z',
//           ground_truth_id: null,
//           confidence: 0.9999961853027344,
//         },
//       ],
//     },
//   },
//   jsonapi: { version: '1.0' },
// };
